import { useState, useEffect, useCallback } from "react";
import { Flame, AlertTriangle, CalendarDays, BellRing, Phone, CheckCircle2, Loader2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getToday, updateActivity } from "@/lib/api";
import { fmtDt } from "@/components/LeadActivities";
import { toast } from "sonner";

const fmtEur = (n) => n ? new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n) : "—";

const Section = ({ icon: Icon, title, count, tone, children, testid }) => (
  <div data-testid={testid} className="rounded-xl bg-slate-900 border border-white/10 overflow-hidden">
    <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
      <div className="flex items-center gap-2 font-display font-semibold text-white"><Icon className={`h-4 w-4 ${tone}`} /> {title}</div>
      <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${count ? "bg-white/5 text-white border-white/20" : "text-slate-500 border-white/10"}`}>{count}</span>
    </div>
    <div className="divide-y divide-white/5">{children}</div>
  </div>
);

const Empty = ({ text }) => <div className="px-4 py-6 text-sm text-slate-500 text-center">{text}</div>;

const LeadRow = ({ l, onOpen, extra }) => (
  <button data-testid={`today-lead-${l.id}`} onClick={() => onOpen(l.id)} className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors">
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-white font-medium truncate">{l.nome || "Senza nome"} <span className="text-slate-500 font-normal">· {l.telefono}</span></div>
        <div className="text-xs text-slate-400 truncate">{l.stage} · {fmtEur(l.importo)} {l.azienda && `· ${l.azienda}`}</div>
        {l.criticita_azienda?.length > 0 && <div className="text-[11px] text-amber-300 mt-0.5 flex items-center gap-1"><Building2 className="h-3 w-3" /> Verifica azienda: {l.criticita_azienda[0]}</div>}
      </div>
      {extra}
    </div>
  </button>
);

export default function TodayPanel({ onOpenLead }) {
  const [data, setData] = useState(null);
  const refresh = useCallback(() => getToday().then(setData).catch(() => toast.error("Impossibile caricare la vista Oggi")), []);
  useEffect(() => { refresh(); }, [refresh]);

  const done = async (a) => { await updateActivity(a.id, { done: true }); toast.success("Fatto"); refresh(); };

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;
  const { hot_leads, overdue, today, renewals, counts } = data;
  const ActRow = ({ a, late }) => (
    <div data-testid={`today-activity-${a.id}`} className="px-4 py-3 flex items-center gap-3">
      <button onClick={() => onOpenLead(a.lead_id)} className="flex-1 text-left min-w-0">
        <div className="text-white font-medium truncate">{a.lead?.nome || "Lead"} <span className="text-slate-500 font-normal">· {a.titolo}</span></div>
        <div className={`text-xs ${late ? "text-rose-400" : "text-slate-400"}`}>{fmtDt(a.data)} {a.lead?.telefono && `· ${a.lead.telefono}`} {a.note && `· ${a.note.slice(0, 60)}`}</div>
      </button>
      <Button data-testid={`today-done-${a.id}`} size="sm" variant="ghost" onClick={() => done(a)} className="text-emerald-400 hover:text-emerald-300 gap-1"><CheckCircle2 className="h-4 w-4" /> Fatto</Button>
    </div>
  );

  return (
    <div data-testid="today-panel" className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[["Lead caldi da chiamare", counts.hot, Flame, "text-orange-400"], ["Follow-up in ritardo", counts.overdue, AlertTriangle, "text-rose-400"],
          ["Da fare oggi", counts.today, CalendarDays, "text-sky-400"], ["Rinnovi del mese", counts.renewals, BellRing, "text-accent"]].map(([t, n, I, c]) => (
          <div key={t} className="rounded-xl bg-slate-900 border border-white/10 p-4">
            <div className="flex items-center gap-2 text-xs text-slate-400"><I className={`h-4 w-4 ${c}`} /> {t}</div>
            <div className="font-display text-3xl font-bold text-white mt-1">{n}</div>
          </div>
        ))}
      </div>
      <div className="grid xl:grid-cols-2 gap-6">
        <Section icon={Flame} tone="text-orange-400" title="Lead caldi (ultime 48h)" count={hot_leads.length} testid="today-hot">
          {hot_leads.length === 0 ? <Empty text="Nessun nuovo lead nelle ultime 48 ore" /> : hot_leads.map((l) => (
            <LeadRow key={l.id} l={l} onOpen={onOpenLead} extra={l.contattato
              ? <span className="text-[10px] uppercase font-bold text-emerald-400 shrink-0">contattato</span>
              : <span className="text-[10px] uppercase font-bold text-orange-300 shrink-0 flex items-center gap-1"><Phone className="h-3 w-3" /> chiama ora</span>} />
          ))}
        </Section>
        <Section icon={AlertTriangle} tone="text-rose-400" title="Follow-up in ritardo" count={overdue.length} testid="today-overdue">
          {overdue.length === 0 ? <Empty text="Nessun follow-up in ritardo" /> : overdue.map((a) => <ActRow key={a.id} a={a} late />)}
        </Section>
        <Section icon={CalendarDays} tone="text-sky-400" title="Da fare oggi" count={today.length} testid="today-todo">
          {today.length === 0 ? <Empty text="Nessuna attività programmata per oggi" /> : today.map((a) => <ActRow key={a.id} a={a} />)}
        </Section>
        <Section icon={BellRing} tone="text-accent" title="Rinnovi di questo mese" count={renewals.length} testid="today-renewals">
          {renewals.length === 0 ? <Empty text="Nessun rinnovo da proporre questo mese" /> : renewals.map((r) => (
            <button key={r.id} onClick={() => onOpenLead(r.lead_id)} className="w-full text-left px-4 py-3 hover:bg-white/5">
              <div className="text-white font-medium">{r.nome} <span className="text-slate-500 font-normal">· {r.telefono}</span></div>
              <div className="text-xs text-slate-400">{r.finanziamento} · rinnovabile da {r.data_rinnovabilita} · {r.status === "sent" ? "richiamo inviato" : "richiamo programmato"}</div>
            </button>
          ))}
        </Section>
      </div>
    </div>
  );
}
